import { ViewerSizeType } from '../types';

/**
 * Parameters for calculating the optimal viewer size
 * This function ensures the viewer fills the entire container while maintaining aspect ratio
 * Viewer size is now independent of zoom, providing a stable coordinate system
 */
export type GetViewerSizeParams = {
	/** The actual size of the container element */
	containerSize: { width: number; height: number };
	/** Optional panning offset for diameter calculation */
	panning?: { x: number; y: number };
};

/**
 * Helper function to calculate the optimal viewer size that fills the entire container space
 *
 * This function performs the following calculations:
 * 1. Determines the primary dimension based on container aspect ratios
 * 2. Calculates a single scaling ratio using the larger container dimension
 * 3. Applies the ratio to both dimensions to maintain aspect ratio
 * 4. Calculates center position for proper coordinate system
 * 5. Provides a stable coordinate system independent of zoom level
 *
 * The goal is to maximize the use of available container space while maintaining
 * the aspect ratio and providing a stable coordinate system for zoom operations.
 *
 * @param params - Configuration parameters including container size and optional panning
 * @returns Calculated viewer dimensions, center position, and scaling ratio
 */
export const getViewerSize = (params: GetViewerSizeParams): ViewerSizeType => {
	const { containerSize, panning = { x: 0, y: 0 }} = params;

	// Validate inputs to prevent division by zero or invalid calculations
	if (!containerSize || containerSize.width <= 0 || containerSize.height <= 0) {
		throw new Error('Invalid container size: width and height must be positive numbers');
	}

	// Calculate the greater of container width or height, divided by pixels per inch
	// 96 is the standard CSS pixels per inch
	// 12 converts from screen inches to view inches (1 screen inch = 24 view inches)
	// devicePixelRatio accounts for high-DPI displays (e.g., Retina screens)
	const standardPixelsPerInch = 96;
	const screenToViewRatio = 12;
	const devicePixelRatio = typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1;

	// Calculate effective pixels per inch considering device pixel ratio
	const effectivePixelsPerInch = (standardPixelsPerInch * devicePixelRatio) / screenToViewRatio;

	// Calculate viewer size based purely on container dimensions and PPI
	// No zoom dependency - this provides a stable coordinate system
	const xSize = containerSize.width / effectivePixelsPerInch;
	const ySize = containerSize.height / effectivePixelsPerInch;

	// Calculate the center position (origin at center)
	const centerX = xSize / -2;
	const centerY = ySize / -2;

	// Calculate diameter considering panning offset
	const xWithPanning = xSize + Math.abs(panning.x * 2);
	const yWithPanning = ySize + Math.abs(panning.y * 2);
	const diameter = Math.sqrt((xWithPanning * xWithPanning) + (yWithPanning * yWithPanning));

	const value: ViewerSizeType = {
		width: xSize,
		height: ySize,
		x: centerX,
		y: centerY,
		diameter,
		ratio: containerSize.width / containerSize.height,
		effectivePixelsPerInch
	};

	return value;
};
